import { useEffect, useMemo, useRef } from "react";
import { MapPinOff, X, RefreshCw, Compass, Chrome } from "lucide-react";
import { Button } from "@/components/ui/button";

export type LocationBlockedPlatform = "ios" | "android" | "desktop";

interface LocationBlockedInfoProps {
  /** Optional: jeśli nie podane, instrukcje są dobierane automatycznie na podstawie przeglądarki */
  platform?: LocationBlockedPlatform;
  onClose: () => void;
}

type Browser = "safari-ios" | "safari-mac" | "chrome-android" | "chrome-desktop";

interface BrowserGuide {
  label: string;
  icon: typeof Compass;
  steps: Array<React.ReactNode>;
}

const guides: Record<Browser, BrowserGuide> = {
  "safari-ios": {
    label: "Safari na iPhone / iPad",
    icon: Compass,
    steps: [
      <>Otwórz <strong className="font-semibold text-foreground">Ustawienia</strong> iPhone/iPad</>,
      <>Przewiń do <strong className="font-semibold text-foreground">Safari</strong></>,
      <>Kliknij <strong className="font-semibold text-foreground">Lokalizacja</strong></>,
      <>Wybierz <strong className="font-semibold text-foreground">Pytaj</strong> lub <strong className="font-semibold text-foreground">Zezwalaj</strong></>,
      <>Wróć do przeglądarki i <strong className="font-semibold text-foreground">odśwież stronę</strong></>,
    ],
  },
  "safari-mac": {
    label: "Safari na Macu",
    icon: Compass,
    steps: [
      <>W menu <strong className="font-semibold text-foreground">Safari → Ustawienia</strong></>,
      <>Przejdź do zakładki <strong className="font-semibold text-foreground">Strony</strong> i wybierz <strong className="font-semibold text-foreground">Lokalizacja</strong></>,
      <>Przy tej stronie ustaw <strong className="font-semibold text-foreground">Zezwól</strong></>,
      <><strong className="font-semibold text-foreground">Odśwież stronę</strong></>,
    ],
  },
  "chrome-android": {
    label: "Chrome na Androidzie",
    icon: Chrome,
    steps: [
      <>Kliknij ikonę <strong className="font-semibold text-foreground">kłódki</strong> obok adresu strony w Chrome</>,
      <>Włącz <strong className="font-semibold text-foreground">dostęp do lokalizacji</strong> dla tej strony</>,
      <>Jeśli nadal nie działa, sprawdź: <strong className="font-semibold text-foreground">Ustawienia telefonu → Aplikacje → Chrome → Uprawnienia → Lokalizacja</strong></>,
      <>Wróć do przeglądarki i <strong className="font-semibold text-foreground">odśwież stronę</strong></>,
    ],
  },
  "chrome-desktop": {
    label: "Chrome / Edge na komputerze",
    icon: Chrome,
    steps: [
      <>Kliknij ikonę <strong className="font-semibold text-foreground">kłódki</strong> w pasku adresu</>,
      <>Wybierz <strong className="font-semibold text-foreground">Ustawienia witryny</strong></>,
      <>Przy <strong className="font-semibold text-foreground">Lokalizacja</strong> wybierz <strong className="font-semibold text-foreground">Zezwalaj</strong></>,
      <><strong className="font-semibold text-foreground">Odśwież stronę</strong></>,
    ],
  },
};

function detectBrowser(): Browser {
  if (typeof navigator === "undefined") return "chrome-desktop";
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isMac = /Macintosh/.test(ua) && !isIOS;
  // Safari: zawiera "Safari" i nie zawiera "Chrome"/"CriOS"/"FxiOS"/"EdgiOS"
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|Chromium/.test(ua);

  if (isIOS) {
    // Na iOS wszystkie przeglądarki używają silnika Safari, więc instrukcja Safari iOS pasuje
    return "safari-ios";
  }
  if (isAndroid) return "chrome-android";
  if (isMac && isSafari) return "safari-mac";
  return "chrome-desktop";
}

function platformToBrowser(platform: LocationBlockedPlatform): Browser {
  if (platform === "ios") return "safari-ios";
  if (platform === "android") return "chrome-android";
  return "chrome-desktop";
}

export const LocationBlockedInfo = ({ platform, onClose }: LocationBlockedInfoProps) => {
  const browser = useMemo<Browser>(
    () => (platform ? platformToBrowser(platform) : detectBrowser()),
    [platform]
  );
  const guide = guides[browser];
  const steps = guide.steps;
  const BrowserIcon = guide.icon;
  const subtitle = `Włącz dostęp w ${steps.length} krokach, by zobaczyć schroniska w pobliżu.`;
  const cardRef = useRef<HTMLDivElement>(null);

  // ESC żeby zamknąć + klik poza kartą zamyka
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (cardRef.current && target && !cardRef.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [onClose]);

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="false"
      aria-label="Lokalizacja zablokowana - instrukcja"
      className="group relative w-full overflow-hidden rounded-2xl border border-white/60 bg-card/80 backdrop-blur-xl shadow-glow-soft animate-fade-in motion-safe:transition-shadow motion-safe:duration-500 hover:shadow-glow"
    >
      {/* Dekoracyjne glow blobs */}
      <div className="pointer-events-none absolute -top-16 -right-16 w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-primary/15 blur-2xl motion-safe:animate-[pulse_6s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-walking/10 blur-3xl motion-safe:animate-[pulse_8s_cubic-bezier(0.4,0,0.6,1)_infinite]" />

      {/* Header */}
      <div className="relative flex items-start gap-2.5 sm:gap-3 px-3.5 sm:px-5 pt-3.5 sm:pt-5 pb-2 sm:pb-2.5">
        <span className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/15 ring-1 ring-primary/20 shrink-0 motion-safe:transition-transform motion-safe:duration-500 group-hover:scale-[1.04]">
          <MapPinOff className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-extrabold text-sm sm:text-base text-foreground tracking-tight leading-tight">
            Lokalizacja zablokowana
          </h3>
          <p className="text-[12px] sm:text-sm text-muted-foreground mt-0.5 leading-snug">
            {subtitle}
          </p>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 -mt-0.5 -mr-1 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 motion-safe:transition-all motion-safe:duration-200 hover:rotate-90 active:scale-90"
          aria-label="Zamknij"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Wykryta przeglądarka */}
      <div className="relative px-3.5 sm:px-5 pb-2">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 ring-1 ring-primary/20 px-2.5 py-1 text-[11px] sm:text-xs font-semibold text-primary">
          <BrowserIcon className="w-3.5 h-3.5" />
          <span>Wykryto: {guide.label}</span>
        </div>
      </div>

      {/* Steps z kaskadowym fade-in */}
      <ol className="relative px-3.5 sm:px-5 pb-3 sm:pb-4 space-y-1.5 sm:space-y-2">
        {steps.map((step, i) => (
          <li
            key={`${browser}-${i}`}
            className="flex items-start gap-2.5 sm:gap-3 rounded-xl bg-background/60 border border-border/60 px-2.5 sm:px-3 py-2 sm:py-2.5 motion-safe:animate-fade-in motion-safe:transition-all motion-safe:duration-200 hover:border-primary/30 hover:bg-background/80"
            style={{
              animationDelay: `${80 + i * 60}ms`,
              animationFillMode: "backwards",
            }}
          >
            <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-md sm:rounded-lg bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-[11px] sm:text-xs font-extrabold shadow-sm shrink-0 mt-0.5">
              {i + 1}
            </span>
            <span className="min-w-0 break-words text-[12.5px] sm:text-sm text-muted-foreground leading-snug sm:leading-relaxed">
              {step}
            </span>
          </li>
        ))}
      </ol>

      {/* CTA */}
      <div
        className="relative px-3.5 sm:px-5 pb-3.5 sm:pb-5 motion-safe:animate-fade-in"
        style={{
          animationDelay: `${80 + steps.length * 60}ms`,
          animationFillMode: "backwards",
        }}
      >
        <Button
          size="sm"
          className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow-soft hover:shadow-glow motion-safe:transition-all motion-safe:duration-300 font-semibold active:scale-[0.98]"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="w-4 h-4 mr-1.5 motion-safe:transition-transform motion-safe:duration-500 group-hover:rotate-180" />
          Odśwież stronę
        </Button>
      </div>
    </div>
  );
};
